<template>
  <div class="layout-inner explore_entity_result employer_details">
    <navbar></navbar>
    <div class="layout-container">
      <div class="layout-content">
        <div class="container-fluid flex-grow-1 pg_content_container employer-details-body">
          <template v-if="isLoading">
            <div class="w-100 h-100 d-flex justify-content-center align-items-center">
              <spinner size="large"></spinner>
            </div>
          </template>
          <template v-else>
            <div class="employer-title-row">
              <span class="ic_container">
                <span class="ic ic2_employer" aria-hidden="true"></span>
              </span>
              <div class="employer-title">{{ employerName }}</div>
              <span
                class="ic_bm_container ace_sec_txt"
                :class="{ bookmarked: isEmployerBookmarked }"
                v-if="isBookmarkEnabled"
                @click.stop="toggleEmployerBookmark">
                <span class="ic ic8-bookmark3"></span>
              </span>
            </div>

            <div class="details-tabs" role="tablist">
              <button
                type="button"
                class="details-tab"
                :class="{ 'is-active': activeTab === 'details' }"
                @click="activeTab = 'details'">
                {{ $t('details') }}
              </button>
              <button
                v-if="isSubOrgTabEnabled"
                type="button"
                class="details-tab"
                :class="{ 'is-active': activeTab === 'schools' }"
                @click="activeTab = 'schools'">
                {{ getSubOrgTabLabel }}
              </button>
            </div>

            <div v-show="activeTab === 'details'">
              <div class="employer-header">
                <div
                  class="employer-avatar"
                  :class="{ 'has-logo': employer.logo }"
                  :style="employer.logo ? null : { background: avatarColors[0] }">
                  <img v-if="employer.logo" :src="employer.logo" :alt="employerName">
                  <span v-else>{{ employerInitials(employerName) }}</span>
                </div>
                <button type="button" class="job-count-link" @click="goToJobBoard">
                  <span class="icon-briefcase meta-icon" aria-hidden="true"></span>
                  <span>{{ jobPostedCount }} {{ $t('jobs') }}</span>
                </button>
              </div>
              <div class="meta-grid">
                <div class="meta-item">
                  <span class="ic2_url meta-icon" aria-hidden="true"></span>
                  <a v-if="employer.uri" :href="employer.uri" :title="websiteLinkLabel" target="_blank">{{ websiteLinkLabel }}</a>
                  <span v-else>{{ $t('na') }}</span>
                </div>
                <div class="meta-item">
                  <span class="icon-location meta-icon" aria-hidden="true"></span>
                  <span>{{ city }}</span>
                </div>
                <div class="meta-item">
                  <span class="icon-map meta-icon" aria-hidden="true"></span>
                  <span>{{ state }}</span>
                </div>
                <div class="meta-item">
                  <span class="icon-phone meta-icon" aria-hidden="true"></span>
                  <span>{{ phone }}</span>
                </div>
                <div class="meta-item">
                  <span class="icon-phone meta-icon" aria-hidden="true"></span>
                  <span>{{ fax }}</span>
                </div>
                <div class="meta-item">
                  <span class="icon-user meta-icon" aria-hidden="true"></span>
                  <span>{{ supportName }}</span>
                </div>
                <div class="meta-item">
                  <span class="icon-mail meta-icon" aria-hidden="true"></span>
                  <span>{{ email }}</span>
                </div>
                <div class="meta-item">
                  <span class="ico-map meta-icon" aria-hidden="true"></span>
                  <span>{{ region }}</span>
                </div>
                <div class="meta-item address">
                  <span class="icon-location meta-icon" aria-hidden="true"></span>
                  <span>{{ address }}</span>
                </div>
              </div>
              <h2 class="section-heading">
                <span class="ic2_description meta-icon" aria-hidden="true"></span>
                <span>{{ $t('description') }}</span>
              </h2>
              <div class="employer-description" v-if="employer.description" v-html="employer.description"></div>
              <div v-else class="no_result">{{ $t('noDescriptionForEmployer') }}</div>
            </div>

            <div v-if="isSubOrgTabEnabled && activeTab === 'schools'">
              <div class="school-toolbar">
                <div class="srh_bx school-search">
                  <input
                    type="text"
                    class="search-input"
                    :placeholder="$t('typeToNarrowResults')"
                    v-model.trim="schoolSearchTerms"
                    v-on:keyup.enter="searchSchools"/>
                  <span @click="searchSchools" class="search-icon">
                    <i class="fa fa-search" aria-hidden="true"></i>
                  </span>
                </div>
                <div class="school-count">{{ schoolTotal }} {{ getSubOrgTabLabel }}</div>
              </div>
              <sub-org-list
                :parent-id="employerUuid"
                :search="schoolAppliedSearch"
                :page="schoolPage"
                :page-size="schoolPageSize"
                @paging="onSchoolPaging">
              </sub-org-list>
              <div class="job-pagination">
                <button type="button" class="btn" :disabled="schoolFirstPage" @click="prevSchoolPage">{{ $t('previousPage') }}</button>
                <span class="current-page">{{ schoolPage + 1 }}</span>
                <button type="button" class="btn" :disabled="schoolLastPage" @click="nextSchoolPage">{{ $t('nextPage') }}</button>
              </div>
            </div>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex'
import { PACKAGE } from '../../../package_constants'
import Navbar from '../../misc/components/./Navbar'
import SubOrgList from './SubOrgList'
import employerSettingsFlags from '../employerSettingsFlags'

export default {
  name: 'employerDetails',
  mixins: [employerSettingsFlags],
  components: {
    Navbar,
    SubOrgList
  },
  props: {
    uuid: {
      type: String,
      default: ''
    }
  },
  data () {
    return {
      isLoading: true,
      employer: {},
      activeTab: 'details',
      schoolSearchTerms: '',
      schoolAppliedSearch: '',
      schoolPage: 0,
      schoolPageSize: 10,
      schoolTotal: 0,
      schoolFirstPage: true,
      schoolLastPage: true,
      avatarColors: ['#e08a4a', '#3d5a99', '#9b8ac4', '#4a7c8c']
    }
  },
  mounted () {
    this.loadPage()
  },
  methods: {
    ...mapActions([
      'loadUserObj',
      'loadEmployerOrgInfo',
      'storeJobSelectedFacets',
      'loadCreateBookmarks',
      'loadDeleteBookmark',
      'loadAllBookmarks'
    ]),
    async loadPage () {
      this.isLoading = true
      await this.loadUserObj()
      await this.loadAllBookmarks()
      await this.loadEmployer()
      this.isLoading = false
    },
    loadEmployer () {
      if (!this.uuid) {
        this.employer = {}
        return Promise.resolve()
      }
      return this.loadEmployerOrgInfo(this.uuid).then(res => {
        this.employer = res || {}
      }).catch(() => {
        this.employer = {}
      })
    },
    searchSchools () {
      this.schoolAppliedSearch = this.schoolSearchTerms
      this.schoolPage = 0
    },
    prevSchoolPage () {
      if (this.schoolFirstPage) {
        return
      }
      this.schoolPage -= 1
    },
    nextSchoolPage () {
      if (this.schoolLastPage) {
        return
      }
      this.schoolPage += 1
    },
    onSchoolPaging (meta) {
      this.schoolTotal = (meta && meta.total) || 0
      this.schoolFirstPage = !meta || meta.firstPage
      this.schoolLastPage = !meta || meta.lastPage
      let totalPages = Math.ceil(this.schoolTotal / this.schoolPageSize)
      if (totalPages < 1) {
        totalPages = 1
      }
      if (this.schoolPage > totalPages - 1) {
        this.schoolPage = totalPages - 1
      }
    },
    goToJobBoard () {
      let employerName = this.employer.title || this.employer.displayName
      if (!employerName) {
        return
      }
      this.storeJobSelectedFacets({key: 'employerSelected', value: [employerName]})
      this.$router.push({
        name: 'jobBoard',
        query: {
          employer: employerName
        }
      })
    },
    async toggleEmployerBookmark () {
      if (!this.employer || !this.employer.uuid) {
        return
      }
      if (this.isEmployerBookmarked) {
        await this.loadDeleteBookmark(this.currentBookmark.uuid)
        this.notifySuccess(this.$t('successfullyDeletedBookmark') + ' \'' + this.employerName + '\'')
      } else {
        const bookmark = {
          'source': 'explore',
          'entityType': 'employer',
          'entityTitle': this.employerName,
          'entityUuid': this.employer.uuid,
          'institutionId': this.userObj.institutionId,
          'userId': this.userObj.uuid
        }
        const response = await this.loadCreateBookmarks(bookmark)
        const message = (response ? this.$t('successfullyBookmarked') : this.$t('unableToBookmark')) + ' \'' + this.employerName + '\''
        if (response) {
          this.notifySuccess(message)
        } else {
          this.notifyFailure(message)
        }
      }
      await this.loadAllBookmarks()
    },
    employerInitials (title) {
      if (!title) {
        return ''
      }
      let first = title.trim().split(/\s+/)[0]
      if (first.length <= 4) {
        return first.toUpperCase()
      }
      return first.substring(0, 3).toUpperCase()
    },
    notifySuccess (text) {
      return this.$notify({
        group: 'notify',
        type: 'success',
        title: '',
        text: text
      })
    },
    notifyFailure (text) {
      return this.$notify({
        group: 'notify',
        type: 'error',
        title: '',
        text: text
      })
    }
  },
  computed: {
    ...mapState({
      'bookmarks': state => state.explore.bookmarks,
      'userObj': state => state.login.userObj
    }),
    isSubOrgTabEnabled () {
      return this.isFlagEnabled('Employer Settings', 'Sub Organization tab')
    },
    employerUuid () {
      return this.uuid || (this.employer && this.employer.uuid) || ''
    },
    employerName () {
      return this.employer.displayName || this.employer.title || ''
    },
    websiteLinkLabel () {
      return this.employer.displayName || this.employer.title || this.$t('website')
    },
    isBookmarkEnabled () {
      let bookmarkFeature = this.enabledFeatures && this.enabledFeatures[PACKAGE.FEATURE_IDS.BOOKMARK_FEATURE_ID]
      return !!(bookmarkFeature && bookmarkFeature.enabled)
    },
    currentBookmark () {
      if (!this.employer || !this.employer.uuid || !this.bookmarks || !this.bookmarks.length) {
        return null
      }
      let found = this.bookmarks.find((bookmark) => {
        return bookmark.entityUuid === this.employer.uuid && bookmark.entityType === 'employer'
      })
      return found || null
    },
    isEmployerBookmarked () {
      return !!this.currentBookmark
    },
    supportName () {
      return this.employer.employerSupportName || this.$t('na')
    },
    phone () {
      return this.employer.employerPhone || this.$t('na')
    },
    fax () {
      return this.employer.employerFax || this.$t('na')
    },
    region () {
      return this.employer.region ? ('Region ' + this.employer.region) : this.$t('na')
    },
    email () {
      return this.employer.employerSupportEmail || this.$t('na')
    },
    city () {
      return this.employer.city || this.$t('na')
    },
    state () {
      return this.employer.state || this.$t('na')
    },
    address () {
      return this.employer.address || this.$t('na')
    },
    jobPostedCount () {
      let count = this.$route.query.jobs
      if (count === undefined || count === null || count === '') {
        count = this.employer.jobPosted
      }
      if (count === undefined || count === null || count === '') {
        return 0
      }
      return count
    }
  },
  watch: {
    uuid () {
      this.loadPage()
    }
  }
}
</script>

<style scoped>
  .employer-details-body {
    padding-left: 16px;
    padding-right: 16px;
  }
  .employer-title-row {
    display: flex;
    align-items: center;
    padding: 12px 0 8px;
  }
  .employer-title-row .ic_container {
    margin-right: 8px;
    font-size: 22px;
    color: #91278e;
  }
  .employer-title {
    flex: 1;
    font-size: 18px;
    font-weight: 600;
    color: #4a4a4a;
  }
  .ic_bm_container {
    margin-left: 8px;
  }
  .ic_bm_container.bookmarked {
    color: #3d5a99;
  }
  .details-tabs {
    display: flex;
    gap: 8px;
    margin-bottom: 12px;
  }
  .details-tab {
    height: 32px;
    padding: 0 12px;
    border: 2px solid transparent;
    border-radius: 6px;
    background: #ececec;
    color: #4a4a4a;
    font-size: 14px;
  }
  .details-tab.is-active {
    border-color: rgb(21, 115, 25);
    background: #e4e4e4;
  }
  .employer-header {
    display: flex;
    align-items: center;
    margin-bottom: 12px;
  }
  .employer-avatar {
    width: 84px;
    height: 32px;
    border-radius: 6px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-right: 12px;
    color: #000;
    font-weight: 600;
    overflow: hidden;
    flex-shrink: 0;
  }
  .employer-avatar.has-logo {
    background: transparent;
  }
  .employer-avatar img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
  .job-count-link {
    display: flex;
    align-items: center;
    border: none;
    background: transparent;
    color: #157099;
    padding: 0;
    font-size: 14px;
  }
  .meta-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 8px 12px;
    margin-bottom: 16px;
    font-size: 13px;
    color: #4a4a4a;
  }
  .meta-item {
    display: flex;
    align-items: center;
    min-width: 0;
  }
  .meta-item.address {
    grid-column: 1 / -1;
  }
  .meta-icon {
    margin-right: 8px;
    flex-shrink: 0;
    font-size: 16px;
    color: #4a4a4a;
    line-height: 1;
  }
  .section-heading {
    display: flex;
    align-items: center;
    font-size: 16px;
    font-weight: 600;
    margin-bottom: 8px;
  }
  .employer-description {
    font-size: 14px;
    color: #4a4a4a;
  }
  .school-toolbar {
    margin-bottom: 8px;
  }
  .school-search {
    margin-bottom: 8px;
  }
  .school-count {
    font-size: 13px;
    color: #6b6b6b;
  }
  .job-pagination {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 16px;
    gap: 16px;
  }
  .job-pagination .btn {
    color: #fff;
  }
  .job-pagination .btn:disabled {
    color: #fff;
    opacity: 1;
  }
  .current-page {
    min-width: 24px;
    text-align: center;
  }
</style>



// WEBPACK FOOTER //
// src/app/employer/components/EmployerDetails.vue